import { useState } from "react"
import { useMemo } from "react"
import LiteratureReviewLayout from "../../Components/LiteratureReviewLayout/LiteratureReviewLayout"
import { MenuBook, Download } from "@mui/icons-material"
import { DoiButton } from "../../Components/DoiButton/DoiButton"
import "../ExtractionDefinition/ExtractionDefinition.css"
import "./ExtractionResults.css"
import { useLiteratureReview } from "../../Context/LiteratureReviewContext"

const ExtractionResults = () => {
    const { id, fromStep, workflowData, navigateToStep, getStepsForPage } = useLiteratureReview()
    const [isScreeningSectionHidden, setIsScreeningSectionHidden] = useState(false)
    const steps = useMemo(() => getStepsForPage("data-extraction-results"), [getStepsForPage])

    const screenedArticles = workflowData.screenedArticles ?? []
    const articleUniqueIds = Array.isArray(workflowData.articleUniqueIds) ? workflowData.articleUniqueIds : []
    const extractionArticleValues = workflowData.extractionArticleValues ?? {}
    const fullTextArticleIds = workflowData.fullTextArticleIds ?? null
    const fullTextUploadedCount = workflowData.fullTextUploadedCount ?? null
    const extractionColumns = useMemo(
        () => (Array.isArray(workflowData.extractionColumns) ? workflowData.extractionColumns : []),
        [workflowData.extractionColumns]
    )

    const getFieldValuesForArticleColumn = (articleId, columnId) => {
        if (articleId == null || !columnId) return {}
        const article =
            extractionArticleValues[articleId] ??
            extractionArticleValues[String(articleId)] ??
            extractionArticleValues[Number(articleId)]
        if (article == null || Array.isArray(article)) return {}
        const columnData = article[columnId] ?? article[String(columnId)]
        return columnData != null && typeof columnData === "object" && !Array.isArray(columnData) ? columnData : {}
    }

    const hasResults =
        articleUniqueIds.length > 0 &&
        (extractionColumns.length > 0 || Object.keys(extractionArticleValues).length > 0)

    const escapeCsvCell = (val) => {
        if (val == null) return ""
        const s = String(val).trim()
        if (s.includes(",") || s.includes('"') || s.includes("\n") || s.includes("\r")) {
            return `"${s.replace(/"/g, '""')}"`
        }
        return s
    }

    const buildCsvContent = () => {
        const headers = [
            "Paper Title",
            "Authors",
            "Journal",
            "Publication Date",
            "Citations",
            "DOI",
        ]
        extractionColumns.forEach((col) => {
            const fields = Array.isArray(col.fields) ? col.fields : []
            if (fields.length === 0) {
                headers.push(col.label ?? col.id ?? "")
            } else {
                fields.forEach((field) => {
                    const label = field.label ?? field.description ?? field.name ?? field.id ?? ""
                    headers.push(`${col.label ?? col.id ?? ""} - ${label}`)
                })
            }
        })
        const rows = [headers.map(escapeCsvCell).join(",")]
        articleUniqueIds.forEach((articleId, rowIndex) => {
            const paper = screenedArticles[rowIndex] ?? {}
            const rawAuthors = paper?.authors
            const authors = Array.isArray(rawAuthors) ? rawAuthors.join(", ") : (rawAuthors ?? "")
            const cells = [
                escapeCsvCell(paper?.title ?? ""),
                escapeCsvCell(authors),
                escapeCsvCell(paper?.journalTitle ?? ""),
                escapeCsvCell(paper?.publicationDate ?? ""),
                escapeCsvCell(paper?.citationCount ?? ""),
                escapeCsvCell(paper?.doi ?? ""),
            ]
            extractionColumns.forEach((col) => {
                const fieldValues = getFieldValuesForArticleColumn(articleId, col.id)
                const fields = Array.isArray(col.fields) ? col.fields : []
                if (fields.length === 0) {
                    const val = fieldValues.value ?? fieldValues ?? ""
                    cells.push(escapeCsvCell(val))
                } else {
                    fields.forEach((field) => {
                        const value = fieldValues[field.name] ?? fieldValues[field.id] ?? ""
                        cells.push(escapeCsvCell(value))
                    })
                }
            })
            rows.push(cells.join(","))
        })
        return rows.join("\r\n")
    }

    const handleDownloadCsv = () => {
        const csv = buildCsvContent()
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" })
        const url = URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.download = `MedRPT-extraction-results-${id ?? "export"}.csv`
        link.click()
        URL.revokeObjectURL(url)
    }

    const mainContent = (
        <div className="extraction-definition-content">
            {hasResults ? (
                <div className="extraction-definition-table-wrapper">
                    <table className="extraction-definition-table">
                        <thead>
                            <tr>
                                <th className="extraction-definition-paper-col">Paper</th>
                                {extractionColumns.map((col) => (
                                    <th key={col.id}>{col.label ?? col.id ?? ""}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {articleUniqueIds.map((articleId, rowIndex) => {
                                const paper = screenedArticles[rowIndex] ?? {}
                                const title = paper?.title ?? ""
                                const rawAuthors = paper?.authors
                                const authors = Array.isArray(rawAuthors) ? rawAuthors.join(", ") : (rawAuthors ?? "")
                                const journal = paper?.journalTitle ?? ""
                                const publicationDate = paper?.publicationDate ?? ""
                                const citations = paper?.citationCount ?? 0
                                const doi = paper?.doi ?? ""
                                return (
                                    <tr key={String(articleId)}>
                                        <td className="extraction-definition-paper-cell">
                                            <div className="extraction-definition-paper-card">
                                                <div className="extraction-definition-paper-title">{title}</div>
                                                {authors && <div className="extraction-definition-paper-authors">{authors}</div>}
                                                <div className="extraction-definition-paper-meta">
                                                    <span className="paper-meta-item">
                                                        <MenuBook sx={{ fontSize: 14, color: "#666", mr: 0.5, verticalAlign: "middle" }} />
                                                        {journal},{" "}
                                                    </span>
                                                    <span className="paper-meta-item">{publicationDate}, </span>
                                                    <span className="paper-meta-item">{citations} citations, </span>
                                                    {doi && <DoiButton doi={doi} />}
                                                </div>
                                            </div>
                                        </td>
                                        {extractionColumns.map((col) => {
                                            const fieldValues = getFieldValuesForArticleColumn(articleId, col.id)
                                            const fields = Array.isArray(col.fields) ? col.fields : []
                                            const isYesNoMaybe = col.answerStructure === "yes_no_maybe"
                                            return (
                                                <td key={col.id} className="extraction-definition-field-cell">
                                                    {isYesNoMaybe ? (
                                                        <span>{fieldValues["answer"] ?? "—"}</span>
                                                    ) : (
                                                        <ul className="extraction-definition-field-list">
                                                            {fields.map((field, idx) => {
                                                                const value = fieldValues[field.name] ?? fieldValues[field.id] ?? "—"
                                                                const description = field.label ?? field.description ?? field.name ?? ""
                                                                return (
                                                                    <li key={field.name ?? field.id ?? idx}>
                                                                        <span className="extraction-definition-field-sep">-</span> {description}
                                                                        <span className="extraction-definition-field-sep">:</span> {String(value)}
                                                                    </li>
                                                                )
                                                            })}
                                                        </ul>
                                                    )}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="extraction-results-empty">
                    <p>No extraction results to display. Run extraction from the Extraction Definition step.</p>
                </div>
            )}
        </div>
    )

    const handleBack = () => {
        navigateToStep("data-extraction-definition")
    }

    const handleNext = () => {
        navigateToStep("research-report")
    }

    const sidebar = (
        <div className="extraction-results-sidebar">
            <div className="extraction-results-sidebar-section">
                <h3 className="extraction-results-sidebar-title">
                    Extraction Details
                    <span
                        className="extraction-results-sidebar-icon"
                        onClick={(e) => {
                            e.stopPropagation()
                            setIsScreeningSectionHidden(false)
                        }}
                    >
                        ℹ️
                    </span>
                </h3>
            </div>

            {!isScreeningSectionHidden && (
                <div className="extraction-results-sidebar-section">
                    <div className="extraction-results-sidebar-header">
                        <h4>Review and export extraction</h4>
                    </div>
                    <div className="extraction-results-sidebar-content">
                        <p>
                            MedRPT will extract data from screened-in sources and continue processing them even if you close this tab. When finished, you can export the extraction results as a CSV.
                        </p>
                        <a
                            href="#"
                            className="extraction-results-sidebar-link"
                            onClick={(e) => {
                                e.preventDefault()
                                setIsScreeningSectionHidden(true)
                            }}
                        >
                            Hide
                        </a>
                    </div>
                </div>
            )}

            <div className="extraction-results-sidebar-section">
                <h3 className="extraction-results-sidebar-title">Status</h3>
                <div className="extraction-results-status">
                    <span className="extraction-results-status-badge">Extraction complete</span>
                </div>
            </div>

            <div className="extraction-results-sidebar-section">
                <h3 className="extraction-results-sidebar-title">Export</h3>
                <div className="extraction-results-export-buttons">
                    <button
                        type="button"
                        className="extraction-results-export-btn-card"
                        onClick={handleDownloadCsv}
                        disabled={!hasResults}
                    >
                        <Download className="extraction-results-export-btn-icon" />
                        <div className="extraction-results-export-btn-text">
                            <span className="extraction-results-export-btn-label">CSV</span>
                            <span className="extraction-results-export-btn-subtitle">Comma-separated</span>
                        </div>
                    </button>
                </div>
            </div>

        </div>
    )

    return (
        <LiteratureReviewLayout
            projectTitle={workflowData.litreviewtitle || "Extraction Results"}
            editableTitle={!!id}
            steps={steps}
            mainContent={mainContent}
            sidebar={sidebar}
            onBack={handleBack}
            backLabel=""
            onNext={handleNext}
            nextLabel="Generate report"
            showBack={true}
            showNext={hasResults}
        />
    )
}

export default ExtractionResults
